<script setup lang="ts">
import { computed, nextTick, ref, watch, watchEffect } from 'vue';
import ClientType from '@/modules/common/enums/client-type.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import { createSchema } from '@/modules/ui/models/schema/schema.ts';
import { SchemaCombobox } from '@/modules/ui/models/schema/schema-combobox.ts';
import { SchemaHost } from '@/modules/ui/models/schema/schema-host.ts';
import { SchemaInput } from '@/modules/ui/models/schema/schema-input.ts';
import { useWizard, useWizardStep } from '@/providers/wizard.ts';
import { useCounterpartyMaster } from '@/modules/counterparty/counterparty-master/counterparty-master.ts';
import { useCounterpartyMasterAccess } from '@/modules/counterparty/counterparty-master/composables/use-counterparty-master-access.ts';
import { CounterpartyApi } from '@/modules/counterparty/api/counterparty.api.ts';
import type { CounterpartyInnResult } from '@/modules/counterparty/types/counterparty-inn-result.type.ts';
import { LEGAL_DATA_MAX_HEIGHT_PX } from '@/modules/counterparty/counterparty-master/consts/legal-data-layout.ts';
import {
  isCounterpartyInnSearchType,
  isCounterpartyLegalEntityType,
} from '@/modules/counterparty/utils/counterparty-legal-fields-visibility.util.ts';
import UiForm from '@/modules/ui/components/ui/UiForm.vue';
import InnSearchBlock from '@/modules/counterparty/counterparty-master/components/InnSearchBlock.vue';
import CounterpartyLegalDataBlock from '@/modules/counterparty/counterparty-master/components/CounterpartyLegalDataBlock.vue';
import CounterpartyNameField from '@/modules/counterparty/counterparty-master/components/CounterpartyNameField.vue';

type FormData = {
  type: Option<number> | null;
  code: string;
};

const { formData } = useCounterpartyMaster();
const { next } = useWizard();
const { isValid, showErrors } = useWizardStep();
const masterAccess = useCounterpartyMasterAccess();
const formValid = ref(false);
const nameFieldValid = ref(false);
const searchInn = ref(formData.value.inn);
const isSearching = ref(false);
const isInnDuplicate = ref(false);
const legalDataValid = ref(false);
const innSearchApplied = ref(false);
const appliedSnapshot = ref<string | null>(null);
const legalDataResetKey = ref(0);
const legalDataFieldMaxHeight = `${LEGAL_DATA_MAX_HEIGHT_PX}px`;
const pendingAdvance = ref(false);

const showInnSearch = computed(() => isCounterpartyInnSearchType(formData.value.type?.code));

const showLegalEntityFields = computed(() => isCounterpartyLegalEntityType(formData.value.type?.code));

watchEffect(() => {
  if (!showInnSearch.value) {
    isValid.value = formValid.value && nameFieldValid.value;
    return;
  }

  isValid.value = formValid.value && legalDataValid.value;
});

watch(showErrors, (show, wasShow) => {
  if (!show) {
    pendingAdvance.value = false;
    return;
  }

  if (!wasShow) {
    pendingAdvance.value = !isValid.value;
  }
});

watch(
  () => [
    formData.value.name,
    formData.value.inn,
    formData.value.kpp,
    formData.value.ogrn,
    formData.value.okpo,
    formData.value.okato,
    formData.value.code,
    formData.value.legalForm?.code ?? null,
    searchInn.value,
  ],
  () => {
    pendingAdvance.value = false;
  }
);

watch(isValid, (valid) => {
  if (!valid || !pendingAdvance.value) {
    return;
  }

  void nextTick(() => {
    if (!pendingAdvance.value || !isValid.value) {
      return;
    }

    pendingAdvance.value = false;
    next();
  });
});

const schema = computed(() =>
  createSchema(
    new SchemaCombobox({
      name: 'type',
      placeholder: 'counterparty.master.steps.name.field.type',
      options: CounterpartyApi.getTypeOptions,
      chooseAfterLoad: formData.value.type?.code ?? ClientType.Client,
      updateAfterLoad: true,
      required: true,
      expanded: true,
      indent: 'spacer',
    }),
    showInnSearch.value &&
      new SchemaHost({
        name: 'innHost',
        slot: 'inn',
      }),
    showInnSearch.value &&
      new SchemaHost({
        name: 'legalDataHost',
        slot: 'legalData',
        indent: 'spacer',
      }),
    !showInnSearch.value &&
      new SchemaHost({
        name: 'nameHost',
        slot: 'name',
        indent: 'spacer',
      }),
    masterAccess.value.code &&
      new SchemaInput({
        name: 'code',
        placeholder: 'counterparty.master.steps.name.field.code',
        maxLength: 255,
      })
  )
);

const formModel = computed({
  get: (): FormData => ({
    type: formData.value.type,
    code: formData.value.code,
  }),
  set: ({ type, code }: FormData) => {
    const prevTypeId = formData.value.type?.code;
    formData.value = {
      ...formData.value,
      type,
      code,
    };

    if (prevTypeId != null && type?.code !== prevTypeId) {
      onTypeChange();
    }
  },
});

function legalSnapshot() {
  return JSON.stringify({
    name: formData.value.name,
    inn: formData.value.inn,
    legalForm: formData.value.legalForm?.code ?? null,
    kpp: formData.value.kpp,
    ogrn: formData.value.ogrn,
    okpo: formData.value.okpo,
    okato: formData.value.okato,
  });
}

function clearLegalDataFields() {
  formData.value = {
    ...formData.value,
    name: '',
    inn: '',
    legalForm: null,
    kpp: '',
    ogrn: '',
    okpo: '',
    okato: '',
  };
}

function resetInnSearch() {
  searchInn.value = '';
  isInnDuplicate.value = false;
  innSearchApplied.value = false;
  appliedSnapshot.value = null;
}

function onTypeChange() {
  showErrors.value = false;
  isSearching.value = false;
  nameFieldValid.value = false;
  legalDataValid.value = false;
  clearLegalDataFields();
  resetInnSearch();
  formData.value = {
    ...formData.value,
    code: '',
  };
}

function onApplyInnResult(result: CounterpartyInnResult) {
  if (!showInnSearch.value) {
    return;
  }

  searchInn.value = result.inn;
  showErrors.value = false;
  formData.value = {
    ...formData.value,
    name: result.name,
    inn: result.inn,
    legalForm:
      showLegalEntityFields.value && result.legalForm ? { code: result.legalForm, label: result.legalForm } : null,
    kpp: showLegalEntityFields.value ? (result.kpp ?? '') : '',
    ogrn: result.ogrn ?? '',
    okpo: '',
    okato: '',
  };
  innSearchApplied.value = true;
  appliedSnapshot.value = legalSnapshot();
}

function resetLegalDataState() {
  showErrors.value = false;
  clearLegalDataFields();
  innSearchApplied.value = false;
  appliedSnapshot.value = null;
}

function onClearLegalData() {
  resetLegalDataState();
  resetInnSearch();
}

function onInnDuplicate() {
  resetLegalDataState();
  legalDataResetKey.value += 1;
}

function onLegalDataChange() {
  if (appliedSnapshot.value && legalSnapshot() === appliedSnapshot.value) {
    return;
  }

  resetInnSearch();
}
</script>

<template>
  <div
    class="step-name"
    :style="{ '--legal-data-field-max-height': legalDataFieldMaxHeight }"
  >
    <UiForm
      v-model="formModel"
      v-model:valid="formValid"
      v-model:show-errors="showErrors"
      type="compact"
      :schema
    >
      <template #inn>
        <InnSearchBlock
          :key="String(formData.type?.code ?? 'inn')"
          v-model:inn="searchInn"
          v-model:loading="isSearching"
          v-model:duplicate="isInnDuplicate"
          :applied="innSearchApplied"
          @apply="onApplyInnResult"
          @clear-legal-data="onInnDuplicate"
        />
      </template>

      <template #name>
        <CounterpartyNameField
          :key="String(formData.type?.code ?? 'name')"
          v-model="formData.name"
          v-model:valid="nameFieldValid"
          :show-errors="showErrors"
        />
      </template>

      <template #legalData>
        <CounterpartyLegalDataBlock
          :key="`${String(formData.type?.code ?? 'legal')}-${legalDataResetKey}`"
          v-model:name="formData.name"
          v-model:legal-form="formData.legalForm"
          v-model:inn="formData.inn"
          v-model:kpp="formData.kpp"
          v-model:ogrn="formData.ogrn"
          v-model:okpo="formData.okpo"
          v-model:okato="formData.okato"
          v-model:valid="legalDataValid"
          :loading="isSearching"
          :show-errors="showErrors"
          :show-legal-entity-fields="showLegalEntityFields"
          @clear="onClearLegalData"
          @change="onLegalDataChange"
        />
      </template>
    </UiForm>
  </div>
</template>

<style scoped lang="scss">
.step-name {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;

  :deep(.ui-form) {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    height: 100%;
  }

  :deep(.ui-form__fields) {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
  }

  :deep(.ui-field._type-compact) {
    flex: 0 0 auto;

    &:not(:last-child) {
      margin-bottom: 16px;
    }
  }

  :deep(.ui-field:has(.legal-data-block)) {
    display: flex;
    flex: 0 1 auto;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    max-height: var(--legal-data-field-max-height);
    overflow: hidden;

    &:not(:last-child) {
      margin-bottom: 16px;
    }

    .ui-schema-host {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      min-width: 0;
      min-height: 0;
      max-height: 100%;
    }

    .legal-data-block {
      flex: 1 1 auto;
      min-height: 0;
      max-height: 100%;
    }
  }
}
</style>
